import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Avatar, Clickable, Col, Logo, Text, TermsAgreement } from 'components';
import {
  CallToActionLogin,
  SignupWithEmail,
  SignupWithSocials
} from 'screens/Profile/components/Auth';
import { REFERRAL_COLORS } from './theme';

const AUTH_STEPS = {
  SIGN_UP_WITH_SOCIALS: 'SIGN_UP_WITH_SOCIALS',
  SIGN_UP_WITH_EMAIL: 'SIGN_UP_WITH_EMAIL',
  CALL_TO_ACTION_LOGIN: 'CALL_TO_ACTION_LOGIN'
};

const Shell = styled.div`
  min-height: calc(100vh - 74px);
  background: radial-gradient(
    120% 60% at 50% -10%,
    ${REFERRAL_COLORS.glow} 0%,
    ${REFERRAL_COLORS.bg} 55%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
`;

const AvatarHalo = styled.div`
  position: relative;
  display: inline-flex;
  padding: 14px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-image: radial-gradient(${REFERRAL_COLORS.mutedText} 1px, transparent 1.5px);
    background-size: 9px 9px;
    opacity: 0.4;
    -webkit-mask-image: radial-gradient(
      circle,
      transparent 56%,
      black 60%,
      black 78%,
      transparent 82%
    );
    mask-image: radial-gradient(circle, transparent 56%, black 60%, black 78%, transparent 82%);
  }
`;

const Pill = styled.div`
  border: 1px solid ${REFERRAL_COLORS.cta};
  border-radius: 999px;
  padding: 4px 14px;
  margin-bottom: 16px;
  margin-top: 10px;
`;

const CardStage = styled.div`
  position: relative;
  width: 100%;
  max-width: 340px;
  background: ${REFERRAL_COLORS.white};
  border-radius: 20px;
  padding: 28px 24px 24px;
  box-shadow:
    0 24px 60px -20px rgba(65, 31, 74, 0.65),
    0 8px 24px rgba(0, 0, 0, 0.25);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 20px;
    right: 20px;
    height: 3px;
    border-radius: 0 0 3px 3px;
    background: ${REFERRAL_COLORS.cta};
  }
`;

const ReferralAuthGate = ({ team, onAuthenticated }) => {
  const { t } = useTranslation();
  const { isMobile } = useSelector((state) => state.appState);
  const [currentAuthStep, setCurrentAuthStep] = useState(AUTH_STEPS.SIGN_UP_WITH_SOCIALS);

  // Overrides the default post-auth admin/home redirect (src/containers/User.js)
  // by setting the one field it checks first: user.navigateTo. Without this,
  // signing up here still sends the user to /admin or /home instead of back to
  // this referral page.
  const navigateTo = `${window.location.pathname.replace(/^\//, '')}${window.location.search}`;

  const renderSignupFooter = () => (
    <>
      <TermsAgreement
        col={REFERRAL_COLORS.mutedText}
        maxWid={isMobile ? '100%' : '70%'}
        marg="20px 0 0"
      />
      <Clickable
        marg="12px 0 0"
        onClick={() => setCurrentAuthStep(AUTH_STEPS.CALL_TO_ACTION_LOGIN)}
      >
        <Text b2 col={REFERRAL_COLORS.white}>
          {t('auth.alreadyHaveAccount')}{' '}
          <Text spanned b2 med col={REFERRAL_COLORS.white}>
            {t('auth.logIn')}
          </Text>
        </Text>
      </Clickable>
    </>
  );

  // Returns just the active step's own component — CardStage supplies the
  // light background every step needs, and the terms/login footer (which
  // sets its own white text for the dark page background) is rendered
  // separately outside CardStage so the two never mix colors.
  const renderStepBody = () => {
    switch (currentAuthStep) {
      case AUTH_STEPS.SIGN_UP_WITH_SOCIALS:
        return (
          <SignupWithSocials
            isMobile={isMobile}
            onHandleSignUpWithEmail={() => setCurrentAuthStep(AUTH_STEPS.SIGN_UP_WITH_EMAIL)}
            redirectUrl={navigateTo}
          />
        );
      case AUTH_STEPS.SIGN_UP_WITH_EMAIL:
        return (
          <SignupWithEmail
            otherUser={team}
            actionType="fancam-challenge"
            onHandleGoBack={() => setCurrentAuthStep(AUTH_STEPS.SIGN_UP_WITH_SOCIALS)}
            followAthlete={onAuthenticated}
            navigateTo={navigateTo}
          />
        );
      case AUTH_STEPS.CALL_TO_ACTION_LOGIN:
        return (
          <CallToActionLogin
            otherUser={team}
            actionType="fancam-challenge"
            setCurrentAuthStep={setCurrentAuthStep}
            authSteps={AUTH_STEPS}
            followAthlete={onAuthenticated}
            navigateTo={navigateTo}
          />
        );
      default:
        return null;
    }
  };

  const isLoginStep = currentAuthStep === AUTH_STEPS.CALL_TO_ACTION_LOGIN;
  const showSignupFooter = !isLoginStep;

  return (
    <Shell>
      <Col centerAll wid="100%" minWid={!isMobile ? '500px' : '100%'}>
        <AvatarHalo>
          <Avatar user={team} showVerified />
        </AvatarHalo>
        <Text h3 center col={REFERRAL_COLORS.white} marg="16px 0 2px">
          {team?.name}
        </Text>
        <Pill>
          <Text c1 bold spacing="0.06em" col={REFERRAL_COLORS.white}>
            FAN CHALLENGE
          </Text>
        </Pill>
        <Col center wid={isMobile ? '280px' : '320px'} marg="0 0 28px">
          <Text b2 center col={REFERRAL_COLORS.mutedText}>
            {t(isLoginStep ? 'referral.authGateBodySignIn' : 'referral.authGateBody', {
              teamName: team?.name || team?.username
            })}
          </Text>
        </Col>
        <CardStage>{renderStepBody()}</CardStage>
        {showSignupFooter && renderSignupFooter()}
        <Col center marg="32px 0 0">
          <Logo wid="140px" white />
        </Col>
      </Col>
    </Shell>
  );
};

export default ReferralAuthGate;
